import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { Modal, Field, Th, Td } from "./Customers";
import { Plus, Wrench, ShieldCheck, Star, Image as ImageIcon, BookOpen, Search, ThumbsUp } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const TABS = [
  { id: "pm", label: "Planned Maintenance" },
  { id: "warranty", label: "Warranty Register" },
  { id: "kpis", label: "Technician KPIs" },
  { id: "kb", label: "Knowledge Base" },
  { id: "nps", label: "NPS Surveys" },
];

export default function Operations() {
  const [tab, setTab] = useState("pm");
  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]" data-testid="operations-page">
      <PageHeader title="Operations" subtitle="Planned maintenance, warranties, technician KPIs, knowledge base & NPS surveys." />
      <div className="mt-6 flex flex-wrap gap-1 border-b border-[#DEE2E6]">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`ops-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === t.id ? "border-[#FF4F00] text-[#0A0A0A]" : "border-transparent text-[#495057] hover:text-[#0A0A0A]"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "pm" && <PMTab />}
        {tab === "warranty" && <WarrantyTab />}
        {tab === "kpis" && <KpisTab />}
        {tab === "kb" && <KBTab />}
        {tab === "nps" && <NPSTab />}
      </div>
    </div>
  );
}

function PMTab() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => Promise.all([
    api.get("/engineering/pm-schedules").then(({ data }) => setRows(data)),
    api.get("/customers").then(({ data }) => setCustomers(data)),
  ]);
  useEffect(() => { load(); }, []);
  const spawn = async (sid) => { await api.post(`/engineering/pm-schedules/${sid}/spawn`); load(); };
  const spawnAll = async () => { const { data } = await api.post("/engineering/pm-schedules/spawn-due"); alert(`Spawned ${data.count} job(s)`); load(); };
  return (
    <>
      <div className="flex justify-end gap-2 mb-4">
        <button onClick={spawnAll} data-testid="spawn-due-btn" className="inline-flex items-center gap-2 border border-[#0A0A0A] px-4 py-2.5 text-sm"><Wrench size={14}/> Spawn all due</button>
        <button onClick={() => setOpen(true)} data-testid="add-pm-btn" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm"><Plus size={14}/> New PM schedule</button>
      </div>
      <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="pm-table">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
            <tr><Th>Title</Th><Th>Customer</Th><Th>Frequency</Th><Th>Next due</Th><Th>Last spawned</Th><Th></Th></tr>
          </thead>
          <tbody>
            {rows.map((s, i) => {
              const due = new Date(s.next_due) <= new Date();
              return (
                <tr key={s.id} className="border-b border-[#DEE2E6]" data-testid={`pm-row-${i}`}>
                  <Td><div className="font-medium">{s.title}</div><div className="text-xs text-[#495057] truncate max-w-[300px]">{s.instructions}</div></Td>
                  <Td>{s.customer_name}</Td>
                  <Td className="text-xs uppercase tracking-[0.15em]">{s.frequency}</Td>
                  <Td className={due ? "text-[#D90429] font-bold" : ""}>{s.next_due}</Td>
                  <Td className="text-xs text-[#495057]">{s.last_spawned ? new Date(s.last_spawned).toLocaleDateString() : "—"}</Td>
                  <Td><button onClick={() => spawn(s.id)} data-testid={`pm-spawn-${i}`} className="px-2 py-1 text-xs bg-[#0A0A0A] text-white">Spawn job</button></Td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#495057]">No PM schedules.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <PMForm customers={customers} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </>
  );
}

function PMForm({ customers, onClose, onSaved }) {
  const [f, setF] = useState({ customer_id: "", title: "", frequency: "monthly", next_due: "", instructions: "" });
  const [err, setErr] = useState("");
  const save = async (e) => { e.preventDefault(); try { await api.post("/engineering/pm-schedules", f); onSaved(); } catch (e) { setErr(e?.response?.data?.detail || "Failed"); } };
  return (
    <Modal title="New PM schedule" onClose={onClose} testid="pm-modal" wide>
      <form onSubmit={save} className="grid grid-cols-2 gap-4" data-testid="pm-form">
        <label className="block col-span-2"><span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Customer</span>
          <select required value={f.customer_id} onChange={(e) => setF({ ...f, customer_id: e.target.value })} data-testid="pm-cust" className="w-full border border-[#DEE2E6] px-3 py-2 text-sm">
            <option value="">— select —</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <div className="col-span-2"><Field label="Title" required value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="pm-title" /></div>
        <label className="block"><span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Frequency</span>
          <select value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })} data-testid="pm-freq" className="w-full border border-[#DEE2E6] px-3 py-2 text-sm">
            {["weekly", "monthly", "quarterly", "biannual", "annual"].map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </label>
        <Field label="Next due (YYYY-MM-DD)" required value={f.next_due} onChange={(v) => setF({ ...f, next_due: v })} testid="pm-due" />
        <div className="col-span-2"><Field label="Instructions" value={f.instructions} onChange={(v) => setF({ ...f, instructions: v })} testid="pm-instr" /></div>
        {err && <div className="col-span-2 text-sm text-[#D90429]">{err}</div>}
        <div className="col-span-2 flex justify-end gap-2"><button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button><button type="submit" data-testid="pm-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Save</button></div>
      </form>
    </Modal>
  );
}

function WarrantyTab() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/engineering/warranties").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  return (
    <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="warranty-table">
      <table className="w-full text-sm">
        <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
          <tr><Th>Customer</Th><Th>Item</Th><Th>Serial #</Th><Th>Coverage</Th><Th>Expires</Th><Th align="right">Days left</Th><Th>Status</Th></tr>
        </thead>
        <tbody>
          {rows.map((w, i) => (
            <tr key={w.id} className="border-b border-[#DEE2E6]" data-testid={`warranty-row-${i}`}>
              <Td>{w.customer_name}</Td>
              <Td><div className="font-medium">{w.item_name}</div><div className="text-xs text-[#495057] font-mono">{w.item_sku}</div></Td>
              <Td className="font-mono text-xs">{w.serial_no}</Td>
              <Td className="text-xs">{w.coverage}</Td>
              <Td>{w.end_date}</Td>
              <Td align="right" className={`tabular-nums ${w.days_remaining < 30 ? "text-[#D90429]" : ""}`}>{w.days_remaining}</Td>
              <Td><span className={`text-[10px] uppercase tracking-[0.15em] border px-2 py-0.5 ${w.status === "expired" ? "border-[#D90429] text-[#D90429]" : w.status === "expiring_soon" ? "border-[#F5B700] text-[#F5B700]" : "border-[#0B7A75] text-[#0B7A75]"}`}>{w.status}</span></Td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#495057]">No warranties.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function KpisTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/engineering/technician-kpis").then(({ data }) => setRows(data)); }, []);
  return (
    <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="tech-kpis">
      <table className="w-full text-sm">
        <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
          <tr><Th>Technician</Th><Th align="right">Total jobs</Th><Th align="right">Completed</Th><Th align="right">In progress</Th><Th align="right">Avg resolution hrs</Th><Th align="right">First-time fix %</Th><Th align="right">Parts used</Th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.technician_id} className="border-b border-[#DEE2E6]" data-testid={`kpi-row-${i}`}>
              <Td><div className="font-medium">{r.technician_name}</div></Td>
              <Td align="right" className="tabular-nums">{r.jobs_total}</Td>
              <Td align="right" className="tabular-nums font-bold text-[#0B7A75]">{r.jobs_completed}</Td>
              <Td align="right" className="tabular-nums">{r.jobs_in_progress}</Td>
              <Td align="right" className="tabular-nums">{r.avg_resolution_hours ?? "—"}</Td>
              <Td align="right" className="tabular-nums">{r.first_time_fix_pct == null ? "—" : `${r.first_time_fix_pct}%`}</Td>
              <Td align="right" className="tabular-nums">{r.parts_used_total}</Td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#495057]">No technician data.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function KBTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const load = () => api.get(`/crm/kb${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [q]);
  return (
    <>
      <div className="flex justify-between items-end gap-3 mb-4">
        <label className="block flex-1 max-w-md"><span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Search</span>
          <div className="relative"><Search size={14} className="absolute left-3 top-3 text-[#495057]"/><input value={q} onChange={(e) => setQ(e.target.value)} data-testid="kb-search" placeholder="Title, body, tags..." className="w-full bg-white border border-[#DEE2E6] pl-8 pr-3 py-2 text-sm"/></div>
        </label>
        <button onClick={() => setOpen(true)} data-testid="add-kb-btn" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm"><Plus size={14}/> New article</button>
      </div>
      <div className="grid md:grid-cols-2 gap-3" data-testid="kb-list">
        {rows.map((a) => (
          <button key={a.id} onClick={async () => { const { data } = await api.get(`/crm/kb/${a.id}`); setView(data); }} data-testid={`kb-article-${a.id}`} className="text-left border border-[#0A0A0A] bg-white p-4 hover:bg-[#F8F9FA]">
            <div className="flex items-start justify-between"><div><span className="text-[10px] uppercase tracking-[0.15em] text-[#495057]">{a.category}</span><div className="font-bold mt-1">{a.title}</div></div><BookOpen size={16} className="text-[#FF4F00]"/></div>
            <div className="text-xs text-[#495057] mt-2 line-clamp-2">{a.body}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#495057] mt-3">{a.tags?.join(" · ")} · {a.views || 0} views</div>
          </button>
        ))}
        {rows.length === 0 && <div className="col-span-2 p-8 text-center text-[#495057] border border-[#DEE2E6]">No articles match.</div>}
      </div>
      {open && <KBForm onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {view && (
        <Modal title={view.title} onClose={() => setView(null)} testid="kb-view-modal" wide>
          <div className="text-xs uppercase tracking-[0.15em] text-[#495057] mb-2">{view.category} · {view.tags?.join(" · ")}</div>
          <div className="whitespace-pre-wrap text-sm leading-relaxed">{view.body}</div>
        </Modal>
      )}
    </>
  );
}

function KBForm({ onClose, onSaved }) {
  const [f, setF] = useState({ title: "", category: "General", tags: "", body: "" });
  const save = async (e) => { e.preventDefault(); await api.post("/crm/kb", { ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) }); onSaved(); };
  return (
    <Modal title="New KB article" onClose={onClose} testid="kb-modal" wide>
      <form onSubmit={save} className="space-y-4" data-testid="kb-form">
        <Field label="Title" required value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="kb-title" />
        <div className="grid grid-cols-2 gap-3"><Field label="Category" value={f.category} onChange={(v) => setF({ ...f, category: v })} testid="kb-cat" /><Field label="Tags (comma-separated)" value={f.tags} onChange={(v) => setF({ ...f, tags: v })} testid="kb-tags" /></div>
        <label className="block"><span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Body</span>
          <textarea required value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={8} data-testid="kb-body" className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm"/>
        </label>
        <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button><button type="submit" data-testid="kb-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Publish</button></div>
      </form>
    </Modal>
  );
}

function NPSTab() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => Promise.all([
    api.get("/crm/nps/summary").then(({ data }) => setSummary(data)),
    api.get("/crm/nps").then(({ data }) => setRows(data)),
    api.get("/customers").then(({ data }) => setCustomers(data)),
  ]);
  useEffect(() => { load(); }, []);
  const submit = async (token, score) => { await api.post("/crm/nps/submit", { token, score, comment: "" }); load(); };
  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={() => setOpen(true)} data-testid="send-nps-btn" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm"><ThumbsUp size={14}/> Send NPS survey</button>
      </div>
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="nps-summary">
          <Kpi label="NPS Score" value={summary.nps != null ? summary.nps : "—"} tone={summary.nps >= 50 ? "good" : summary.nps >= 0 ? "warn" : "bad"} />
          <Kpi label="Responses" value={summary.responses} />
          <Kpi label="Promoters (9-10)" value={`${summary.promoters} (${summary.promoters_pct || 0}%)`} tone="good" />
          <Kpi label="Passives (7-8)" value={`${summary.passives} (${summary.passives_pct || 0}%)`} />
          <Kpi label="Detractors (0-6)" value={`${summary.detractors} (${summary.detractors_pct || 0}%)`} tone="bad" />
        </div>
      )}
      <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="nps-table">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]"><tr><Th>Customer</Th><Th>Channel</Th><Th>Sent</Th><Th align="right">Score</Th><Th>Status</Th><Th>Quick submit</Th></tr></thead>
          <tbody>
            {rows.map((s, i) => (
              <tr key={s.id} className="border-b border-[#DEE2E6]" data-testid={`nps-row-${i}`}>
                <Td>{s.customer_name}</Td>
                <Td className="text-xs uppercase tracking-[0.15em]">{s.channel}</Td>
                <Td className="text-xs text-[#495057]">{new Date(s.sent_at).toLocaleDateString()}</Td>
                <Td align="right" className="tabular-nums font-bold">{s.score ?? "—"}</Td>
                <Td><span className={`text-[10px] uppercase tracking-[0.15em] border px-2 py-0.5 ${s.status === "submitted" ? "border-[#0B7A75] text-[#0B7A75]" : "border-[#F5B700] text-[#F5B700]"}`}>{s.status}</span></Td>
                <Td>{s.status === "sent" && (<select onChange={(e) => submit(s.token, parseInt(e.target.value))} defaultValue="" data-testid={`nps-submit-${i}`} className="border border-[#DEE2E6] px-2 py-1 text-xs"><option value="" disabled>Score</option>{[...Array(11).keys()].map((n) => <option key={n} value={n}>{n}</option>)}</select>)}</Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#495057]">No surveys yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <NPSSendForm customers={customers} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </>
  );
}

function NPSSendForm({ customers, onClose, onSaved }) {
  const [f, setF] = useState({ customer_id: "", channel: "email" });
  const save = async (e) => { e.preventDefault(); await api.post("/crm/nps/send", f); onSaved(); };
  return (
    <Modal title="Send NPS survey" onClose={onClose} testid="nps-modal">
      <form onSubmit={save} className="space-y-4" data-testid="nps-form">
        <label className="block"><span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Customer</span>
          <select required value={f.customer_id} onChange={(e) => setF({ ...f, customer_id: e.target.value })} data-testid="nps-cust" className="w-full border border-[#DEE2E6] px-3 py-2 text-sm">
            <option value="">— select —</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="block"><span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Channel</span>
          <select value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })} data-testid="nps-channel" className="w-full border border-[#DEE2E6] px-3 py-2 text-sm">
            <option value="email">Email</option><option value="whatsapp">WhatsApp</option>
          </select>
        </label>
        <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button><button type="submit" data-testid="nps-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Send</button></div>
      </form>
    </Modal>
  );
}

function Kpi({ label, value, tone }) {
  const cls = tone === "good" ? "border-[#0B7A75]" : tone === "warn" ? "border-[#F5B700]" : tone === "bad" ? "border-[#D90429]" : "border-[#DEE2E6]";
  return (
    <div className={`border bg-white p-4 ${cls}`} data-testid={`kpi-${(label || "").toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-[#495057]">{label}</div>
      <div className="mt-1 text-xl font-bold tabular-nums">{value}</div>
    </div>
  );
}
